import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup, Label, Input, FormFeedback, Button, Card, CardBody, CardHeader, Collapse } from 'reactstrap';
import { CoreMeitnerExportConfiguration, meitnerExportEnvironmentTypeOptions, meitnerExportGroupTypesOptions, MeitnerGroupTypes } from '../../../../../../models/CoreMeitnerModels';
import EnumInput from '../../../../../misc/EnumInput';
import BoolInput from '../../../../../misc/BoolInput';
import MeitnerUser from './MeitnerUserConfiguration';
import MeitnerExportSchoolUnits from './MeitnerExportSchoolUnits';
import { CoreExportModuleSchoolUnit } from '../../../../../../models/CoreModels';
import NullableInput from '../../../../../misc/NullableInput';


const maxGroupNameAffixLength = 128;

const groupNameAffixFields = [
    'classGroupNamePrefix',
    'classGroupNameSuffix',
    'departmentGroupNamePrefix',
    'departmentGroupNameSuffix',
    'mentorGroupNamePrefix',
    'mentorGroupNameSuffix',
    'educationGroupNamePrefix',
    'educationGroupNameSuffix',
] as const;

export const validator = (configuration: CoreMeitnerExportConfiguration, active: boolean) => {
    const validation: string[] = [];

    if (active) {
        if (configuration.environment === 0) {
            validation.push('environment');
        }
        if (!configuration.clientId || configuration.clientId.trim() === "") {
            validation.push('clientId');
        }
        if (!configuration.clientSecret || configuration.clientSecret.trim() === "") {
            validation.push('clientSecret');
        }
    }

    const employeeGracePeriod = configuration.employeeConfiguration.gracePeriod;
    const studentGracePeriod = configuration.studentConfiguration.gracePeriod;
    const guardianGracePeriod = configuration.guardianConfiguration.gracePeriod;

    if ((Number.isFinite(employeeGracePeriod) && employeeGracePeriod! < 0)
        || Number.isNaN(employeeGracePeriod)) {
        validation.push('employeeConfiguration.gracePeriod');
    }

    if ((Number.isFinite(studentGracePeriod) && studentGracePeriod! < 0)
        || Number.isNaN(studentGracePeriod)) {
        validation.push('studentConfiguration.gracePeriod');
    }

    if ((Number.isFinite(guardianGracePeriod) && guardianGracePeriod! < 0)
        || Number.isNaN(guardianGracePeriod)) {
        validation.push('guardianConfiguration.gracePeriod');
    }

    groupNameAffixFields.forEach((field) => {
        const value = configuration[field];
        if (value && value.length > maxGroupNameAffixLength) {
            validation.push(field);
        }
    });

    return validation;
};

export const warningMessages = (originalConfiguration: CoreMeitnerExportConfiguration, updatedConfiguration: CoreMeitnerExportConfiguration) => {
    const warnings: string[] = [];

    const groupTypes = [
        MeitnerGroupTypes.Class,
        MeitnerGroupTypes.Childcare,
        MeitnerGroupTypes.Mentor,
        MeitnerGroupTypes.Education,
    ];
    const hasRemovedGroupType = groupTypes.some(groupType =>
        (updatedConfiguration.groupTypes & groupType) < (originalConfiguration.groupTypes & groupType)
    );
    if (hasRemovedGroupType) {
        warnings.push('Om du tar bort en grupptyp kommer dessa att arkiveras');
    }
    return warnings;
};


interface MeitnerExportConfigurationProps {
    disabled: boolean
    configuration: CoreMeitnerExportConfiguration
    updatePart: (part: Partial<CoreMeitnerExportConfiguration>) => void
    exportSchoolUnits: CoreExportModuleSchoolUnit[]
    active: boolean
    onValidChange: (arg: boolean) => void 
};


const MeitnerExportConfiguration = (props: MeitnerExportConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);

    const [open, setOpen] = useState<boolean>(true);
    const [groupConfigOpen, setGroupConfigOpen] = useState<boolean>(true);

    const validation = validator(props.configuration, props.active);

    const groupNameAffixRows = [
        {
            label: t('meitnerExportConfiguration.groupNameAffixes.groupTypes.class', { ns: 'configuration' }),
            prefixKey: 'classGroupNamePrefix',
            suffixKey: 'classGroupNameSuffix',
        },
        {
            label: t('meitnerExportConfiguration.groupNameAffixes.groupTypes.department', { ns: 'configuration' }),
            prefixKey: 'departmentGroupNamePrefix',
            suffixKey: 'departmentGroupNameSuffix',
        },
        {
            label: t('meitnerExportConfiguration.groupNameAffixes.groupTypes.mentor', { ns: 'configuration' }),
            prefixKey: 'mentorGroupNamePrefix',
            suffixKey: 'mentorGroupNameSuffix',
        },
        {
            label: t('meitnerExportConfiguration.groupNameAffixes.groupTypes.education', { ns: 'configuration' }),
            prefixKey: 'educationGroupNamePrefix',
            suffixKey: 'educationGroupNameSuffix',
        }
    ] as const;

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration, props.active]);

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('modules.MeitnerExport.title', { ns: 'common' })}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>

                    <EnumInput
                        title={t('meitnerExportConfiguration.environment.title', { ns: 'configuration' })}
                        value={props.configuration.environment}
                        options={meitnerExportEnvironmentTypeOptions.map(x => ({
                            value: x,
                            text: t(`meitnerExportConfiguration.environment.options.${x}`, { ns: 'configuration' }),
                        }))}
                        onChange={(name, value) => props.updatePart({ [name]: value })}
                        disabled={props.disabled}
                        name='environment'
                        invalid={validation.indexOf('environment') !== -1}
                        invalidFeedback={t('meitnerExportConfiguration.environment.formFeedback', { ns: 'configuration' })}
                    />

                    <h5>{t('meitnerExportConfiguration.accessTitle', { ns: 'configuration' })}</h5>
                    <FormGroup>
                        <Label for="clientId">{t('meitnerExportConfiguration.clientId.title', { ns: 'configuration' })}</Label>
                        <Input
                            type="text"
                            name="clientId"
                            id="clientId"
                            placeholder={t('meitnerExportConfiguration.clientId.title', { ns: 'configuration' })}
                            value={props.configuration.clientId || ''}
                            disabled={props.disabled}
                            onChange={(e) => props.updatePart({ clientId: e.target.value })}
                            invalid={validation.indexOf('clientId') !== -1}
                            autoComplete="off"
                        />
                        <FormFeedback>{t('meitnerExportConfiguration.clientId.formFeedback', { ns: 'configuration' })}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="clientSecret">{t('meitnerExportConfiguration.clientSecret.title', { ns: 'configuration' })}</Label>
                        <Input
                            type="text"
                            name="clientSecret"
                            id="clientSecret"
                            placeholder={t('meitnerExportConfiguration.clientSecret.title', { ns: 'configuration' })}
                            value={props.configuration.clientSecret || ''}
                            disabled={props.disabled}
                            onChange={(e) => props.updatePart({ clientSecret: e.target.value })}
                            invalid={validation.indexOf('clientSecret') !== -1}
                            autoComplete="off"
                        />
                        <FormFeedback>{t('meitnerExportConfiguration.clientSecret.formFeedback', { ns: 'configuration' })}</FormFeedback>
                    </FormGroup>

                    <Card className="mb-3">
                        <CardHeader tag="h5">
                            <Button outline color="secondary" style={{ width: '30px', float: 'right', lineHeight: '0.8', padding: '0.5rem' }} onClick={() => setGroupConfigOpen(!groupConfigOpen)}>{groupConfigOpen ? '-' : '+'}</Button>
                            {t('meitnerExportConfiguration.groupConfiguration.title', { ns: 'configuration' })}
                        </CardHeader>
                        <Collapse isOpen={groupConfigOpen}>
                            <CardBody>
                                <EnumInput
                                    title={t('meitnerExportConfiguration.groupTypes.title', { ns: 'configuration' })}
                                    value={props.configuration.groupTypes}
                                    options={meitnerExportGroupTypesOptions.map(x => ({
                                        value: x,
                                        text: t(`meitnerExportConfiguration.groupTypes.options.${x}`, { ns: 'configuration' }),
                                    }))}
                                    onChange={(name, value) => props.updatePart({ [name]: value })}
                                    disabled={props.disabled}
                                    name='groupTypes'
                                    multi={true}
                                />

                                <BoolInput
                                    title={t('meitnerExportConfiguration.sendModerators', { ns: 'configuration' })}
                                    name="sendModerators"
                                    value={props.configuration.sendModerators}
                                    disabled={props.disabled}
                                    updateValue={(name, value) => props.updatePart({ [name]: value })}
                                />

                                <h5>{t('meitnerExportConfiguration.groupNameAffixes.title', { ns: 'configuration' })}</h5>
                                <small className="d-block mb-2">{t('meitnerExportConfiguration.groupNameAffixes.description', { ns: 'configuration' })}</small>
                                <div className="table-responsive mb-0">
                                    <table className="table table-sm table-borderless align-middle mb-0">
                                        <thead>
                                            <tr>
                                                <th>{t('meitnerExportConfiguration.groupNameAffixes.columns.groupType', { ns: 'configuration' })}</th>
                                                <th>{t('meitnerExportConfiguration.groupNameAffixes.columns.prefix', { ns: 'configuration' })}</th>
                                                <th>{t('meitnerExportConfiguration.groupNameAffixes.columns.suffix', { ns: 'configuration' })}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {groupNameAffixRows.map((row) => (
                                                <tr key={row.prefixKey}>
                                                    <td>{row.label}</td>
                                                    <td>
                                                        <NullableInput
                                                            disabled={props.disabled}
                                                            checkboxTitle={t('meitnerExportConfiguration.groupNameAffixes.enablePrefix', { ns: 'configuration' })}
                                                            value={props.configuration[row.prefixKey] as string | null | undefined}
                                                            onChange={(name, value) => props.updatePart({ [name]: value })}
                                                            name={row.prefixKey}
                                                            maxLength={maxGroupNameAffixLength}
                                                            placeholder={t('meitnerExportConfiguration.groupNameAffixes.prefixPlaceholder', { ns: 'configuration' })}
                                                            invalidFeedback={t('meitnerExportConfiguration.groupNameAffixes.maxLengthFeedback', { ns: 'configuration', maxLength: maxGroupNameAffixLength })}
                                                            invalid={validation.indexOf(row.prefixKey) !== -1}
                                                            className="mb-0"
                                                            inputClassName="w-100"
                                                        />
                                                    </td>
                                                    <td>
                                                        <NullableInput
                                                            disabled={props.disabled}
                                                            checkboxTitle={t('meitnerExportConfiguration.groupNameAffixes.enableSuffix', { ns: 'configuration' })}
                                                            value={props.configuration[row.suffixKey] as string | null | undefined}
                                                            onChange={(name, value) => props.updatePart({ [name]: value })}
                                                            name={row.suffixKey}
                                                            maxLength={maxGroupNameAffixLength}
                                                            placeholder={t('meitnerExportConfiguration.groupNameAffixes.suffixPlaceholder', { ns: 'configuration' })}
                                                            invalidFeedback={t('meitnerExportConfiguration.groupNameAffixes.maxLengthFeedback', { ns: 'configuration', maxLength: maxGroupNameAffixLength })}
                                                            invalid={validation.indexOf(row.suffixKey) !== -1}
                                                            className="mb-0"
                                                            inputClassName="w-100"
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Collapse>
                    </Card>

                    <MeitnerUser
                        user={props.configuration.employeeConfiguration}
                        disabled={props.disabled}
                        name="employeeConfiguration"
                        displayName={t('meitnerExportConfiguration.employeeConfiguration', { ns: 'configuration' })}
                        updateValue={x => props.updatePart({ employeeConfiguration: x })}
                        validation={validation}
                    />

                    <MeitnerUser
                        user={props.configuration.studentConfiguration}
                        disabled={props.disabled}
                        name="studentConfiguration"
                        displayName={t('meitnerExportConfiguration.studentConfiguration', { ns: 'configuration' })}
                        updateValue={x => props.updatePart({ studentConfiguration: x })}
                        validation={validation}
                    />

                    <MeitnerUser
                        user={props.configuration.guardianConfiguration}
                        disabled={props.disabled}
                        name="guardianConfiguration"
                        displayName={t('meitnerExportConfiguration.guardianConfiguration', { ns: 'configuration' })}
                        updateValue={x => props.updatePart({ guardianConfiguration: x })}
                        validation={validation}
                        isGuardianConfig={true}
                    />

                    <MeitnerExportSchoolUnits
                        disabled={props.disabled}
                        configuration={props.configuration}
                        exportSchoolUnits={props.exportSchoolUnits}
                        updateValue={x => props.updatePart({ meitnerSchoolUnits: x })}
                    />
                </CardBody>
            </Collapse>
        </Card>
    );
};

export default MeitnerExportConfiguration;
